import type { FableOpsAutomationView } from '../../hooks/useFableLab';
import { operationalIssueLabel } from './fableLabels';

export default function FableOpsAutomation({ data, error }: Props) {
  const ops = data?.opsReport ?? null;
  const weekly = data?.weeklyReview ?? null;
  const alert = data?.blockerAlert ?? null;

  return (
    <section className="fable-panel">
      <div className="fable-panel-head fable-ops-head">
        <div>
          <h3>자동 점검 결과</h3>
          <p>오늘 실행, 매수부터 손익 계산까지의 전체 과정, 주간 검토와 오류 알림을 함께 확인합니다.</p>
        </div>
        <span className={`fable-ops-pill ${alert?.severity || 'warn'}`}>
          {statusLabel(data)}
        </span>
      </div>
      {error && <p className="fable-error">API 오류: 자동 운영 루프 — {error}</p>}
      <div className="fable-ops-grid">
        <OpsMetric
          label="오늘 실행 결과"
          value={ops?.ready_for_stage_a_daily_ops ? '통과' : '조치 필요'}
          detail={ops?.run
            ? `${ops.run.trading_date} · ${ops.run.model_output_status}`
            : '실행 기록 없음'}
        />
        <OpsMetric
          label="전체 과정 점검"
          value={ops?.run?.market_payload_coverage.complete ? '필요 시세 충분' : '필요 시세 부족'}
          detail={coverageText(data)}
        />
        <OpsMetric
          label="주간 검토"
          value={`${weekly?.reviewed_run_count ?? 0}회 검토`}
          detail={backlogText(data)}
        />
        <OpsMetric
          label="오류 알림"
          value={alert?.alert_required ? severityLabel(alert.severity) : '문제 없음'}
          detail={blockerText(data)}
        />
      </div>
      <div className="fable-ops-lists">
        <OpsList title="필요 조치" items={ops?.ops_actions_required || []} />
        <OpsList title="개선 후보" items={weekly?.improvement_backlog || []} />
        <OpsList title="진행을 막는 문제" items={alert?.blockers || []} />
      </div>
    </section>
  );
}

function OpsMetric({ label, value, detail }: OpsMetricProps) {
  return (
    <div className="fable-ops-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  );
}

function OpsList({ title, items }: OpsListProps) {
  return (
    <div className="fable-ops-list">
      <h4>{title}</h4>
      {items.length ? (
        <ul>{items.map((item) => <li key={item} title={item}>{operationalIssueLabel(item)}</li>)}</ul>
      ) : (
        <p>없음</p>
      )}
    </div>
  );
}

function statusLabel(data: FableOpsAutomationView | null) {
  const severity = data?.blockerAlert.severity;
  if (severity === 'critical') return '즉시 확인';
  if (severity === 'warning') return '확인 필요';
  return data?.opsReport.ready_for_stage_a_daily_ops ? '문제 없음' : '점검 중';
}

function coverageText(data: FableOpsAutomationView | null) {
  const coverage = data?.opsReport.run?.market_payload_coverage;
  if (!coverage) return '데이터 없음';
  return `시작 가격 ${coverage.usable_open_count}건 · 손익 계산 가능 ${coverage.usable_ohlc_next_open_count}건`;
}

function backlogText(data: FableOpsAutomationView | null) {
  const count = data?.weeklyReview.improvement_backlog.length ?? 0;
  return count ? `${count}개 개선 후보` : '개선 후보 없음';
}

function blockerText(data: FableOpsAutomationView | null) {
  const count = data?.blockerAlert.blockers.length ?? 0;
  return count ? `${count}개 문제` : '문제 없음';
}

function severityLabel(severity: string) {
  if (severity === 'critical') return '즉시 확인';
  if (severity === 'warning') return '확인 필요';
  return '점검 중';
}

type Props = {
  data: FableOpsAutomationView | null;
  error: string | null;
};

type OpsMetricProps = {
  label: string;
  value: string;
  detail: string;
};

type OpsListProps = {
  title: string;
  items: string[];
};
